<template>
	<div class="row">
		<div class="columns column4 highlights-nav">
			<h3>{{ $t('highlightsHeader') }}</h3>
			<div class="highlights-container">
				<div v-for="(category, index) in items" :key="index" class="category">
					<div
						class="highlight-item button-toggle"
						:class="{ active: activeCategory === index + 1 }"
						@click="updateMarkers(parseInt(category.value, 10))"
					>
						<span>{{ category.name }}</span>
					</div>
					<slide-up-down
						:model-value="activeCategory === index + 1"
						class="highlight-content"
						:duration="300"
					>
						<ul>
							<li
								v-for="item in category.items"
								:key="item.ID"
								:data-id="`#huid_${item.ID}`"
								@click="openInfowindow($event)"
							>
								{{ item.header }}
							</li>
						</ul>
					</slide-up-down>
				</div>
			</div>
		</div>
		<div class="columns column8 maps">
			<div id="gmap" ref="gmap" />
		</div>
	</div>
</template>

<script setup>
import { Loader } from '@googlemaps/js-api-loader';
import GoogleMaps from '@becurious/googlemaps';

const props = defineProps({
	items: { type: Array, default: () => [] },
});

const config = useRuntimeConfig();
const gmap = ref(null);
const activeCategory = ref(null);

let map = null;
const mapData = {};

const mapConfig = {
	zoom: 15,
	center: { lat: 52.36518, lng: 4.8732862 },
	// prettier-ignore
	styles: [{ 'featureType': 'administrative', 'elementType': 'all', 'stylers': [{ 'visibility': 'on' }] }, { 'featureType': 'landscape', 'elementType': 'all', 'stylers': [{ 'visibility': 'simplified' }, { 'hue': '#0066ff' }, { 'saturation': 74 }, { 'lightness': 100 }] }, { 'featureType': 'poi', 'elementType': 'all', 'stylers': [{ 'visibility': 'off' }] }, { 'featureType': 'road', 'elementType': 'all', 'stylers': [{ 'visibility': 'simplified' }] }, { 'featureType': 'road.highway', 'elementType': 'all', 'stylers': [{ 'visibility': 'off' }, { 'weight': 0.6 }, { 'saturation': -85 }, { 'lightness': 61 }] }, { 'featureType': 'road.highway', 'elementType': 'geometry', 'stylers': [{ 'visibility': 'on' }] }, { 'featureType': 'road.arterial', 'elementType': 'all', 'stylers': [{ 'visibility': 'off' }] }, { 'featureType': 'road.local', 'elementType': 'all', 'stylers': [{ 'visibility': 'on' }] }, { 'featureType': 'transit', 'elementType': 'all', 'stylers': [{ 'visibility': 'simplified' }] }, { 'featureType': 'water', 'elementType': 'all', 'stylers': [{ 'visibility': 'simplified' }, { 'color': '#5f94ff' }, { 'lightness': 26 }, { 'gamma': 5.86 }] }],
	draggable: true,
	mapTypeControl: false,
	zoomControl: true,
	streetViewControl: true,
	scrollwheel: true,
};

const allHighlights = props.items.map((category) => category.items).flat();

const activeHighlights = computed(() =>
	allHighlights.filter((item) => {
		return parseInt(item.highlightsCategorie.ID, 10) === parseInt(activeCategory.value, 10);
	}),
);

onMounted(async () => {
	const google = await new Loader({
		apiKey: config.public.maps.apiKey,
		authReferrerPolicy: 'origin',
	}).load();
	map = new GoogleMaps(google, gmap.value, mapConfig);
	initializeMap();
});

const initializeMap = () => {
	map.setMarkerIcon({
		src: '/images/marker.png',
		width: 30,
		height: 42,
		anchorX: 15,
		anchorY: 21,
	});

	map.addInfoWindow(
		map.addMarker(Object.values(mapConfig.center), 'Mosaic Hotel'),
		'<h3>Mosaic Hotel</h3>' + '<p class="address">Eerste Constantijn Huygensstraat 10G<br>1054BR Amsterdam</p>',
	);
};

const updateMarkers = (id) => {
	if (!map) {
		return;
	}

	if (id === activeCategory.value) {
		activeCategory.value = null;
	} else {
		activeCategory.value = id;
	}

	map.setMarkerIcon({
		src: '/images/marker-item.png',
		width: 30,
		height: 42,
		anchorX: 15,
		anchorY: 42,
	});

	map.hideMarker('allcategory');

	activeHighlights.value.forEach((highlight) => {
		const marker = map.addMarker(
			[highlight.location.latitude, highlight.location.longitude],
			highlight.header,
			`cat_${highlight.category}`,
		);

		const infowindow = map.addInfoWindow(marker, getInfowindowContent(highlight));

		mapData[`#huid_${highlight.ID}`] = [infowindow, marker];
	});

	if (activeCategory.value) {
		map.autoCenter();
	}
};

const openInfowindow = (event) => {
	const ID = event.target.dataset.id;

	if (ID) {
		const currentMapData = mapData[event.target.dataset.id];
		map.openInfowindow(currentMapData[0], currentMapData[1]);
	}
};

const getInfowindowContent = (highlight) => {
	let html = '<h3>' + highlight.header + '</h3>';

	html += `<p class="address">
			${highlight.location.address}<br>${highlight.location.postalCode} ${highlight.location.city}
			</p>`;

	if (highlight.phonenumber) {
		html += `<p class="phone">${highlight.phonenumber}</p>`;
	}

	if (highlight.distance_public) {
		html += `<p class="directions public"><span class="icon-directions"></span>
				<span>${highlight.distancePublic}</span>
				</p>`;
	}

	if (highlight.distance_walk) {
		html += `<p class="directions walk"><span class="icon-directions"></span>
				<span>${highlight.distanceWalk}</span>
				</p>`;
	}

	if (highlight.url) {
		html += `<p class="website">
				<a href="${highlight.url}" target="_blank" rel="noopener noreferrer">Website</a>
				</p>`;
	}

	return html;
};
</script>

<style lang="scss" scoped>
.highlights-nav {
	padding: 40px;

	li {
		font-size: 18px;
		margin: 10px 0;
	}
}

.highlights-container {
	display: flex;
	flex-wrap: wrap;
	margin: 0;

	.category {
		width: 100%;
		background: none;
		color: var(--body-color);
		padding: 0;
		margin: 3px 0;
		cursor: pointer;
		transition: all 0.3s ease-in-out;

		.highlight-item {
			text-transform: uppercase;
		}

		ul {
			list-style-position: inside;
			padding: 0 10px;
			margin: 10px 0 20px;
		}

		a {
			text-transform: none;
		}

		.button-toggle {
			padding: 15px 20px;
			transition: all 0.3s ease-in-out;
			font-weight: 700;
			border: 1px solid var(--body-color);

			&:hover,
			&.active {
				background: var(--cta-color);
				border-color: var(--cta-color);
				color: #fff;
			}
		}
	}
}

.maps {
	padding: 0;
}

#gmap {
	min-height: 600px;
}

@media (max-width: 1080px) {
	.highlights {
		margin-bottom: 25px;
		justify-content: center;
		gap: 5px;
	}

	.highlights-nav {
		width: 100%;
		padding: 0;
		text-align: left;

		li {
			font-size: 19px;
			margin: 12px 0;
		}
	}

	.highlights li {
		width: 92%;
	}
}
</style>
